import { queryOptions } from '@tanstack/react-query';
import { api, RequestFailed } from './client.js';
import type {
  BrokersResponse,
  CatalogEvent,
  CatalogEventDetail,
  Me,
  MeState,
  PublicConfig,
  StreamsResponse,
} from './types.js';

/**
 * Every server read, defined once. Routes prefetch these in their loaders and components
 * read them with `useSuspenseQuery`, so the two can never disagree about a key.
 *
 * Anything that reaches a broker takes the selected one as an argument and carries it in its
 * key. Two brokers hold different streams, so a cached answer from one is not an answer for
 * the other — switching the picker must refetch, not repaint stale numbers.
 */
export const queries = {
  config: () =>
    queryOptions({
      queryKey: ['config'],
      queryFn: () => api.get<PublicConfig>('/api/config'),
      staleTime: Infinity,
    }),

  /**
   * Who is signed in.
   *
   * The two refusals stay apart all the way to the screen, because they need opposite
   * responses: no session belongs at the central login, and a session without the role must
   * NOT go there — the login would send that person straight back and the two would bounce
   * forever. Collapsing both into `null` is what made that mistake easy to write.
   */
  me: () =>
    queryOptions({
      queryKey: ['me'],
      queryFn: async (): Promise<MeState> => {
        try {
          return { ok: true, me: await api.get<Me>('/api/me') };
        } catch (error) {
          if (error instanceof RequestFailed && error.status === 401) {
            return { ok: false, reason: 'no-session' };
          }
          if (error instanceof RequestFailed && error.status === 403) {
            return { ok: false, reason: 'no-role' };
          }
          throw error;
        }
      },
      retry: false,
      staleTime: 60_000,
    }),

  brokers: () =>
    queryOptions({
      queryKey: ['brokers'],
      queryFn: () => api.get<BrokersResponse>('/api/brokers'),
    }),

  events: () =>
    queryOptions({
      queryKey: ['catalog', 'events'],
      queryFn: () => api.get<CatalogEvent[]>('/api/catalog/events'),
      staleTime: 60_000,
    }),

  eventDetail: (subject: string) =>
    queryOptions({
      queryKey: ['catalog', 'events', subject],
      queryFn: () => api.get<CatalogEventDetail>(`/api/catalog/events/${encodeURIComponent(subject)}`),
      staleTime: 60_000,
    }),

  // What the broker holds changes while you look at it, so this one polls — but only while
  // it is answering. Discovery offers every NATS in the cluster and not all of them are ours;
  // picking one of those used to poll a failing request every five seconds forever, which
  // reads as the app hanging rather than as a broker that cannot serve you.
  streams: (broker: string) =>
    queryOptions({
      queryKey: ['catalog', 'streams', broker],
      queryFn: () => api.get<StreamsResponse>(`/api/catalog/streams?broker=${encodeURIComponent(broker)}`),
      refetchInterval: (query) => (query.state.error ? false : 5_000),
    }),
};
